// hooks/useGroceries.js
import { useState, useCallback } from 'react';
import { groceriesApi } from '../api/groceriesApi';
import { transformItemData } from '../utils/itemUtils';
import i18n from '../i18n';

export const useGroceries = (showToast) => {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await groceriesApi.fetchList();

      const transformedData = data.map(transformItemData);

      setAllItems(transformedData);
      setError(null);
      return transformedData;
    } catch (err) {
      setError(i18n.t('toasts.errorLoadingItems', { msg: err.message }));
      if (showToast) showToast(i18n.t('toasts.errorLoadingItems', { msg: err.message }), 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const addNewItem = useCallback(async (itemData, showToastFn = showToast) => {
    try {
      const result = await groceriesApi.insertItem(itemData);
      if (!result.success) throw new Error(result.message || 'Error adding item');

      const newItem = transformItemData(result.data);

      setAllItems(prev => [...prev, newItem]);

      if (showToastFn) showToastFn(i18n.t('toasts.itemAddedSuccess'), 'success');
      return newItem;
    } catch (err) {
      if (showToastFn) showToastFn(i18n.t('toasts.errorAddingItem', { msg: err.message }), 'error');
      throw err;
    }
  }, [showToast]);

  const updateItem = useCallback(async (itemData, showToastFn = showToast) => {
    try {
      const result = await groceriesApi.editItem(itemData);
      if (!result.success) throw new Error(result.message || 'Error updating item');

      const updatedItem = transformItemData(result.data);

      setAllItems(prev => prev.map(item =>
        item.id === result.data.id ? updatedItem : item
      ));

      if (showToastFn) showToastFn(i18n.t('toasts.itemUpdatedSuccess'), 'success');
      return updatedItem;
    } catch (err) {
      if (showToastFn) showToastFn(i18n.t('toasts.errorUpdatingItem', { msg: err.message }), 'error');
      throw err;
    }
  }, [showToast]);

  const deleteItem = useCallback(async (id, showToastFn = showToast) => {
    try {
      const result = await groceriesApi.deleteItem(id);
      if (!result.success) throw new Error(result.message || 'Error deleting item');

      setAllItems(prev => prev.filter(item => item.id !== id));

      return true;
    } catch (err) {
      if (showToastFn) showToastFn(i18n.t('toasts.errorDeleting', { msg: err.message }), 'error');
      throw err;
    }
  }, [showToast]);

  return {
    allItems,
    setAllItems,
    loading,
    error,
    fetchItems,
    addNewItem,
    updateItem,
    deleteItem
  };
};
